import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSalesReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import Select from "react-select";
import Table from "../../components/advancedTable";

export default function SalesReport() {
    const dispatch = useDispatch();
    const salesReport = useSelector((state) => state.order.order);
    const currentUser = useSelector((state) => state.user.currentUser);
    const isFetching = useSelector((state) => state.order.isFetching);

    const TwoDecimalFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );

    const outright = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "outright"
            : null
    );

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin : null
    );

    const [isLoaded, setIsLoaded] = useState(false);
    const [salesData, setSalesData] = useState([]);
    const [inputs, setInputs] = useState({
        createdBy: outright ? currentUser?._id : "67fdd4e9a85375769663ed9f",
        principle: manager
            ? principle
            : currentUser
            ? currentUser._id
            : undefined,
    });

    const getAdmin = async () => {
        const res = await publicRequest.get("/users/allsuppliers", {
            params: { isAdmin: true },
        });
        setUsersData(res.data);
    };

    const handleUserFilter = (e) => {
        setInputs((prev) => {
            return { ...prev, createdBy: e.value };
        });
    };

    const [usersData, setUsersData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const res = await getSalesReport(dispatch, inputs);
            if (res) {
                setSalesData(res || []);
                setIsLoaded(true);
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const columns = [
        {
            accessor: "staff",
            Header: "Staff",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding font-medium">{value ? value : "-"}</div>
            ),
        },
        {
            accessor: "email",
            Header: "Email",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding text-slate-600">{value ? value : "-"}</div>
            ),
        },
        {
            accessor: "date",
            Header: "Date",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">
                    <span className="inline-flex px-3 py-1 rounded-lg bg-blue-100 text-blue-800 text-sm font-medium">
                        {value ? value : "-"}
                    </span>
                </div>
            ),
        },
        {
            accessor: "monthlySales",
            Header: "Monthly Sales",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding">
                    <span className="text-lg font-bold text-green-600">
                        {params.row.original.monthlySales
                            ? `${
                                  process.env.REACT_APP_COUNTRY === "SINGAPORE" || 
                                  params.row.original.staff === "HAOFA Tourbillon Singapore"
                                      ? "$"
                                      : "RM"
                              } ${TwoDecimalFormatter.format(params.row.original.monthlySales)}`
                            : "RM 0.00"}
                    </span>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            <div className="productList bg-white rounded-3xl shadow-2xl p-8 m-6 border border-slate-200/50">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">Sales Report</h1>
                    <p className="text-slate-600">Track sales performance across all outlets and staff members</p>
                </div>

                {(superadmin || currentUser?.type === "hq") && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700 tracking-wide">Filter by Outlet</label>
                            <div className="luxury-select-wrapper">
                                <Select
                                    options={usersData.map((user, i) => {
                                        return {
                                            label: `${user.name}`,
                                            value: user._id,
                                        };
                                    })}
                                    placeholder="Select Outlet"
                                    onChange={handleUserFilter}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            background: 'linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))',
                                            border: '2px solid rgb(226 232 240)',
                                            borderRadius: '12px',
                                            padding: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                borderColor: 'rgb(59 130 246)',
                                            }
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected ? 'rgb(59 130 246)' : state.isFocused ? 'rgb(239 246 255)' : 'white',
                                            color: state.isSelected ? 'white' : 'rgb(51 65 85)',
                                            fontWeight: '500',
                                            '&:hover': {
                                                backgroundColor: 'rgb(239 246 255)',
                                            }
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {isLoaded && (
                    <Table
                        data={isFetching === false ? salesData : []}
                        columns={columns}
                        pageCount={Math.ceil(salesData.length / 100)}
                        totalCount={salesData.length}
                        updatePagination={() => {}}
                        paginationState={{ page: 1, limit: 100 }}
                        updateLoaded={() => {}}
                        hidePagination={salesData.length <= 100}
                    />
                )}
            </div>
        </div>
    );
}
